import { Ref, useEffect, useRef } from 'react';
import Quill from 'quill';
import QuillDelta from 'quill-delta';
import { createGlobalStyle } from 'styled-components';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';

import { toRgbString } from 'packages/kakapo-ui-kit/helpers/HexColor';

interface ITreamsTheme {
	colors: Record<string, string>;
}

const Link = Quill.import('formats/link');

class CustomLink extends Link {
	static sanitize(url: string) {
		if (!(Link.PROTOCOL_WHITELIST as string[]).some((protocol) => url.startsWith(protocol))) {
			return super.sanitize(`http://${url}`);
		}

		return super.sanitize(url);
	}
}
Quill.register(CustomLink, true);

const BubbleStyle = createGlobalStyle<{ theme: ITreamsTheme }>`
	.ql-bubble .ql-editor {
		border: 1px solid ${(props) => props.theme.colors.lightBlue};
		border-radius: 4px;
		padding: 14px 18px;
		font-family: Source Sans Pro, sans-serif;

		&:focus {
			background: #ffffff;
			border-color: rgba(${(props) => toRgbString(props.theme.colors.primary)}, 0.6);
			box-shadow: 0 1px 6px 0 rgba(${(props) => toRgbString(props.theme.colors.primary)}, 0.6);
		}

		p, li {
			color: ${(props) => props.theme.colors.slateBlue};
			font-size: 15px;
		}

		li {
			padding-left: 30px;
		}

		a {
			color: ${(props) => props.theme.colors.primary};
		}
	}

	.ql-wide {
		width: 100%;
	}
`;

const BoxedStyling = createGlobalStyle<{ theme: ITreamsTheme }>`
	.ql-boxed {
		.ql-wrapper {
			position: relative;
			background-color: ${({ theme }) => theme.colors.whiteBlueLight};
			border: 1px solid ${({ theme }) => theme.colors.lightBlueDark};
			border-radius: 4px;
		}

		.ql-editor,
		.ql-editor.ql-blank {
			min-height: 100px;

			p,
			li {
				color: ${(props) => props.theme.colors.slateBlue};
				font-size: 15px;
			}
		}

		.ql-toolbar.ql-snow {
			display: flex;
			justify-content: flex-start;

			&.ql-snow {
				border: none;
				border-top-left-radius: 3px;
				border-top-right-radius: 3px;
				background-color: ${({ theme }) => theme.colors.lightBlueLight};
				border-bottom: 1px solid ${({ theme }) => theme.colors.lightBlueDark};
			}

			.ql-formats {
				margin: 0 !important;

				button {
					display: flex;
					justify-content: center;
					align-items: center;
					border-radius: 4px;
					margin: 0 0 0 2px;
					height: 32px;
					width: 32px;

					:hover, :focus {
						.ql-fill {
							fill: ${({ theme }) => theme.colors.primary};
						}

						.ql-stroke {
							stroke: ${({ theme }) => theme.colors.primary};
						}
					}

					&.ql-active {
						background-color: ${({ theme }) => theme.colors.primary};

						.ql-fill {
							fill: white;
						}

						.ql-stroke {
							stroke: white;
						}
					}

					svg {
						max-width: 16px;
					}
				}

				.ql-link {
					margin-left: 12px !important;
				}

				.ql-list {
					position: relative;

					&::after {
						background-color: ${({ theme }) => theme.colors.lightBlueDark};
						content: '';
						position: absolute;
						display: inline-block;
						right: -6px;
						height: 100%;
						width: 1px;
					}
				}

				.ql-link {
					margin: 0 0 0 6px;
				}
			}
		}

		.ql-container {
			font-family: Source Sans Pro !important;
		}

		.ql-container.ql-snow {
			border: none !important;
		}
	}

	.ql-wide {
		width: 100%;
	}
`;

const InlineStyling = createGlobalStyle<{ theme: ITreamsTheme }>`
	.ql-inline {
		p,
		li {
			color: ${(props) => props.theme.colors.slateBlue};
			font-size: 15px;
			font-family: 'Source Sans Pro', sans-serif;
		}
	
		p,
		ul {
			margin-bottom: 10px !important;
		}
	
		a {
			color: ${(props) => props.theme.colors.primary} !important;
	
			:hover {
				text-decoration: none !important;
			}
		}

		.ql-toolbar {
			display: none;
			visibility: hidden;
			width: 0;
			height: 0;
		}

		.ql-container {
			border: none !important;
		}

		.ql-editor {
			padding: 0px !important;
			color: ${(props) => props.theme.colors.slateBlue};
			font-family: Source Sans Pro, sans-serif;

			> p:last-child {
				margin-bottom: 0 !important;
			}
		}

		.ql-editor * {
			font-size: 16px !important;
		}

		.ql-container input, .ql-blank, .ql-clipboard, .ql-ui {
			display: none;
		}
	}
`;

const ReadOnlyStyling = createGlobalStyle`
	.ql-readonly {
		cursor: auto;

		.ql-editor {
			overflow: hidden !important;
			overflow-y: hidden !important;
		}

		.ql-editor a {
			cursor: pointer !important;
		}

		.ql-editor * {
			cursor: inherit !important;
		}
	}
`;

export const useQuill = ({
	theme,
	value,
	onChange,
	readOnly,
	autoFocus,
}: {
	theme?: string;
	value: QuillDelta;
	onChange?: (value: QuillDelta) => void;
	readOnly?: boolean;
	autoFocus?: boolean;
}) => {
	const { t } = useTranslation();

	const ref = useRef<HTMLDivElement>(null);
	const quill = useRef<[Quill]>([] as any);

	const ignoreUntil = useRef<{ date: Date | null }>({ date: null });

	useEffect(() => {
		if (!ref.current || quill.current[0]) {
			return;
		}

		quill.current[0] = new Quill(ref.current!, {
			theme: theme ?? 'snow',
			modules: {
				toolbar: [
					['bold', 'italic', 'underline'],
					[{ list: 'bullet' }, 'link'],
				],
			},
			readOnly: readOnly,
			formats: ['bold', 'italic', 'link', 'list', 'underline'],
		});

		if (autoFocus) {
			quill.current[0].focus();
		}

		if (onChange) {
			quill.current[0].on('text-change', (_a, _b, source) => {
				if (source === 'api') {
					return;
				}

				ignoreUntil.current.date = new Date(Date.now() + 100);

				onChange(quill.current[0].getContents() as any);
			});
		}

		const { tooltip } = (quill.current[0] as any).theme;
		const input = tooltip.root.querySelector('input[data-link]');
		input.dataset.link = t('text.editor.create.link.placeholder');
	}, [!!ref.current]);

	useEffect(() => {
		if (!ignoreUntil.current.date || ignoreUntil.current.date < new Date()) {
			quill.current[0]?.setContents(value as any, 'api');
		}
	}, [value]);

	return { ref, quill: quill.current[0] };
};

export default function QuillEditor({
	autoFocus,
	value,
	theme,
	quillRef,
	onChange,
	bubbleStyling,
	boxedStyling,
	inline,
	readOnly,
	wide,
}: {
	autoFocus?: boolean;
	value?: QuillDelta;
	theme?: string;
	quillRef?: Ref<HTMLDivElement>;
	onChange?: (value: QuillDelta) => void;
	bubbleStyling?: boolean;
	boxedStyling?: boolean;
	inline?: boolean;
	readOnly?: boolean;
	wide?: boolean;
}) {
	if (!quillRef && !value) {
		throw new Error('Prop `value` is required when not using a custom `quillRef` prop.');
	}

	const onChangeRef = useRef(onChange);
	onChangeRef.current = onChange;
	const defaultQuill = useQuill({
		theme,
		value: value!,
		onChange: (value) => onChangeRef.current!(value),
		readOnly,
		autoFocus,
	});
	const ref = quillRef ?? defaultQuill.ref;

	return (
		<div
			className={classnames({
				'ql-boxed': boxedStyling,
				'ql-readonly': readOnly,
				'ql-inline': inline,
				'ql-bubble': bubbleStyling,
				'ql-wide': wide,
			})}
		>
			{boxedStyling && <BoxedStyling />}
			{readOnly && <ReadOnlyStyling />}
			{inline && <InlineStyling />}
			<div className="ql-wrapper">
				<link rel="stylesheet" href="//cdn.quilljs.com/1.3.6/quill.bubble.css" />
				{bubbleStyling && <BubbleStyle />}
				<link rel="stylesheet" href="//cdn.quilljs.com/1.3.6/quill.snow.css" />
				<div ref={ref} />
			</div>
		</div>
	);
}
